// Comprehensive ISO 3166-1 alpha-2 country list.
// Includes sanctioned/restricted jurisdictions (RU, BY, IR, KP, CU, SY, etc.) so that
// compliance officers can configure AUTO-FAIL risk rules and screening parameters
// covering them. The `restricted` flag identifies countries that should NOT be
// offered to customers during onboarding/payments — admin compliance flows ignore
// the flag and show every country.

export type Continent = 
  | 'Europe'
  | 'North America'
  | 'Central America & Caribbean'
  | 'South America'
  | 'Asia'
  | 'Middle East'
  | 'Africa'
  | 'Oceania';

export interface Country {
  code: string;
  name: string;
  flag: string;
  continent: Continent;
  /**
   * Marks countries that are sanctioned or otherwise blocked for customer-facing
   * onboarding/payment flows. Admin compliance UIs (risk matrix, screening, etc.)
   * still display them so they can be referenced in rules and watch lists.
   */
  restricted?: boolean;
}

export const CONTINENT_ORDER: Continent[] = [
  'Europe',
  'North America',
  'Central America & Caribbean',
  'South America',
  'Asia',
  'Middle East',
  'Africa',
  'Oceania'
];

export const countries: Country[] = [
  // UK & Ireland (Europe)
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', continent: 'Europe' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', continent: 'Europe' },
  
  // Europe
  { code: 'AL', name: 'Albania', flag: '🇦🇱', continent: 'Europe' },
  { code: 'AD', name: 'Andorra', flag: '🇦🇩', continent: 'Europe' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹', continent: 'Europe' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', continent: 'Europe' },
  { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', continent: 'Europe' },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', continent: 'Europe' },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷', continent: 'Europe' },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾', continent: 'Europe' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', continent: 'Europe' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', continent: 'Europe' },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪', continent: 'Europe' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', continent: 'Europe' },
  { code: 'FR', name: 'France', flag: '🇫🇷', continent: 'Europe' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', continent: 'Europe' },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', continent: 'Europe' },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', continent: 'Europe' },
  { code: 'IS', name: 'Iceland', flag: '🇮🇸', continent: 'Europe' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', continent: 'Europe' },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻', continent: 'Europe' },
  { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', continent: 'Europe' },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹', continent: 'Europe' },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', continent: 'Europe' },
  { code: 'MT', name: 'Malta', flag: '🇲🇹', continent: 'Europe' },
  { code: 'MD', name: 'Moldova', flag: '🇲🇩', continent: 'Europe' },
  { code: 'MC', name: 'Monaco', flag: '🇲🇨', continent: 'Europe' },
  { code: 'ME', name: 'Montenegro', flag: '🇲🇪', continent: 'Europe' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', continent: 'Europe' },
  { code: 'MK', name: 'North Macedonia', flag: '🇲🇰', continent: 'Europe' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', continent: 'Europe' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', continent: 'Europe' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', continent: 'Europe' },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', continent: 'Europe' },
  { code: 'SM', name: 'San Marino', flag: '🇸🇲', continent: 'Europe' },
  { code: 'RS', name: 'Serbia', flag: '🇷🇸', continent: 'Europe' },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰', continent: 'Europe' },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮', continent: 'Europe' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', continent: 'Europe' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', continent: 'Europe' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', continent: 'Europe' },
  { code: 'UA', name: 'Ukraine', flag: '🇺🇦', continent: 'Europe' },
  
  // North America
  { code: 'US', name: 'United States', flag: '🇺🇸', continent: 'North America' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', continent: 'North America' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', continent: 'North America' },
  
  // Central America & Caribbean (excluding sanctioned)
  { code: 'BB', name: 'Barbados', flag: '🇧🇧', continent: 'Central America & Caribbean' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', continent: 'Central America & Caribbean' },
  { code: 'DM', name: 'Dominica', flag: '🇩🇲', continent: 'Central America & Caribbean' },
  { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', continent: 'Central America & Caribbean' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻', continent: 'Central America & Caribbean' },
  { code: 'GD', name: 'Grenada', flag: '🇬🇩', continent: 'Central America & Caribbean' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹', continent: 'Central America & Caribbean' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳', continent: 'Central America & Caribbean' },
  { code: 'JM', name: 'Jamaica', flag: '🇯🇲', continent: 'Central America & Caribbean' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', continent: 'Central America & Caribbean' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷', continent: 'Central America & Caribbean' },
  { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳', continent: 'Central America & Caribbean' },
  { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨', continent: 'Central America & Caribbean' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', continent: 'Central America & Caribbean' },
  
  // South America
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', continent: 'South America' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴', continent: 'South America' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', continent: 'South America' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱', continent: 'South America' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', continent: 'South America' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨', continent: 'South America' },
  { code: 'GY', name: 'Guyana', flag: '🇬🇾', continent: 'South America' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾', continent: 'South America' },
  { code: 'PE', name: 'Peru', flag: '🇵🇪', continent: 'South America' },
  { code: 'SR', name: 'Suriname', flag: '🇸🇷', continent: 'South America' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾', continent: 'South America' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪', continent: 'South America' },
  
  // Oceania (excluding sanctioned)
  { code: 'AU', name: 'Australia', flag: '🇦🇺', continent: 'Oceania' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', continent: 'Oceania' },
  { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', continent: 'Oceania' },
  { code: 'TO', name: 'Tonga', flag: '🇹🇴', continent: 'Oceania' },
  { code: 'TV', name: 'Tuvalu', flag: '🇹🇻', continent: 'Oceania' },
  { code: 'KI', name: 'Kiribati', flag: '🇰🇮', continent: 'Oceania' },
  { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭', continent: 'Oceania' },
  { code: 'FM', name: 'Micronesia', flag: '🇫🇲', continent: 'Oceania' },
  { code: 'NR', name: 'Nauru', flag: '🇳🇷', continent: 'Oceania' },
  { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', continent: 'Oceania' },
  
  // Asia
  { code: 'JP', name: 'Japan', flag: '🇯🇵', continent: 'Asia' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', continent: 'Asia' },
  { code: 'CN', name: 'China', flag: '🇨🇳', continent: 'Asia' },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', continent: 'Asia' },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼', continent: 'Asia' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', continent: 'Asia' },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', continent: 'Asia' },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', continent: 'Asia' },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', continent: 'Asia' },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', continent: 'Asia' },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', continent: 'Asia' },
  { code: 'IN', name: 'India', flag: '🇮🇳', continent: 'Asia' },
  { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', continent: 'Asia' },
  { code: 'PK', name: 'Pakistan', flag: '🇵🇰', continent: 'Asia' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', continent: 'Asia' },
  { code: 'NP', name: 'Nepal', flag: '🇳🇵', continent: 'Asia' },
  { code: 'BT', name: 'Bhutan', flag: '🇧🇹', continent: 'Asia' },
  { code: 'MV', name: 'Maldives', flag: '🇲🇻', continent: 'Asia' },
  { code: 'KH', name: 'Cambodia', flag: '🇰🇭', continent: 'Asia' },
  { code: 'LA', name: 'Laos', flag: '🇱🇦', continent: 'Asia' },
  { code: 'BN', name: 'Brunei', flag: '🇧🇳', continent: 'Asia' },
  { code: 'MN', name: 'Mongolia', flag: '🇲🇳', continent: 'Asia' },
  { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', continent: 'Asia' },
  { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', continent: 'Asia' },
  { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', continent: 'Asia' },
  { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', continent: 'Asia' },
  { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', continent: 'Asia' },
  { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', continent: 'Asia' },
  { code: 'AM', name: 'Armenia', flag: '🇦🇲', continent: 'Asia' },
  { code: 'GE', name: 'Georgia', flag: '🇬🇪', continent: 'Asia' },
  
  // Middle East (excluding sanctioned)
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', continent: 'Middle East' },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', continent: 'Middle East' },
  { code: 'QA', name: 'Qatar', flag: '🇶🇦', continent: 'Middle East' },
  { code: 'KW', name: 'Kuwait', flag: '🇰🇼', continent: 'Middle East' },
  { code: 'BH', name: 'Bahrain', flag: '🇧🇭', continent: 'Middle East' },
  { code: 'OM', name: 'Oman', flag: '🇴🇲', continent: 'Middle East' },
  { code: 'JO', name: 'Jordan', flag: '🇯🇴', continent: 'Middle East' },
  { code: 'LB', name: 'Lebanon', flag: '🇱🇧', continent: 'Middle East' },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', continent: 'Middle East' },
  { code: 'TR', name: 'Turkey', flag: '🇹🇷', continent: 'Middle East' },
  { code: 'IQ', name: 'Iraq', flag: '🇮🇶', continent: 'Middle East' },
  
  // Africa (excluding sanctioned)
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', continent: 'Africa' },
  { code: 'EG', name: 'Egypt', flag: '🇪🇬', continent: 'Africa' },
  { code: 'MA', name: 'Morocco', flag: '🇲🇦', continent: 'Africa' },
  { code: 'TN', name: 'Tunisia', flag: '🇹🇳', continent: 'Africa' },
  { code: 'DZ', name: 'Algeria', flag: '🇩🇿', continent: 'Africa' },
  { code: 'NG', name: 'Nigeria', flag: '🇳🇬', continent: 'Africa' },
  { code: 'GH', name: 'Ghana', flag: '🇬🇭', continent: 'Africa' },
  { code: 'KE', name: 'Kenya', flag: '🇰🇪', continent: 'Africa' },
  { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', continent: 'Africa' },
  { code: 'UG', name: 'Uganda', flag: '🇺🇬', continent: 'Africa' },
  { code: 'RW', name: 'Rwanda', flag: '🇷🇼', continent: 'Africa' },
  { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', continent: 'Africa' },
  { code: 'SN', name: 'Senegal', flag: '🇸🇳', continent: 'Africa' },
  { code: 'CI', name: 'Ivory Coast', flag: '🇨🇮', continent: 'Africa' },
  { code: 'CM', name: 'Cameroon', flag: '🇨🇲', continent: 'Africa' },
  { code: 'AO', name: 'Angola', flag: '🇦🇴', continent: 'Africa' },
  { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', continent: 'Africa' },
  { code: 'ZM', name: 'Zambia', flag: '🇿🇲', continent: 'Africa' },
  { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', continent: 'Africa' },
  { code: 'BW', name: 'Botswana', flag: '🇧🇼', continent: 'Africa' },
  { code: 'NA', name: 'Namibia', flag: '🇳🇦', continent: 'Africa' },
  { code: 'MU', name: 'Mauritius', flag: '🇲🇺', continent: 'Africa' },
  { code: 'MG', name: 'Madagascar', flag: '🇲🇬', continent: 'Africa' },
  { code: 'MW', name: 'Malawi', flag: '🇲🇼', continent: 'Africa' },
  { code: 'BJ', name: 'Benin', flag: '🇧🇯', continent: 'Africa' },
  { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', continent: 'Africa' },
  { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', continent: 'Africa' },
  { code: 'GA', name: 'Gabon', flag: '🇬🇦', continent: 'Africa' },
  { code: 'GM', name: 'Gambia', flag: '🇬🇲', continent: 'Africa' },
  { code: 'GN', name: 'Guinea', flag: '🇬🇳', continent: 'Africa' },
  { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', continent: 'Africa' },
  { code: 'LS', name: 'Lesotho', flag: '🇱🇸', continent: 'Africa' },
  { code: 'LR', name: 'Liberia', flag: '🇱🇷', continent: 'Africa' },
  { code: 'ML', name: 'Mali', flag: '🇲🇱', continent: 'Africa' },
  { code: 'MR', name: 'Mauritania', flag: '🇲🇷', continent: 'Africa' },
  { code: 'NE', name: 'Niger', flag: '🇳🇪', continent: 'Africa' },
  { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', continent: 'Africa' },
  { code: 'SS', name: 'South Sudan', flag: '🇸🇸', continent: 'Africa' },
  { code: 'SZ', name: 'Eswatini', flag: '🇸🇿', continent: 'Africa' },
  { code: 'TG', name: 'Togo', flag: '🇹🇬', continent: 'Africa' },
  { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', continent: 'Africa' },
  { code: 'KM', name: 'Comoros', flag: '🇰🇲', continent: 'Africa' },
  { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹', continent: 'Africa' },
  { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', continent: 'Africa' },
  { code: 'CD', name: 'Democratic Republic of the Congo', flag: '🇨🇩', continent: 'Africa' },

  // ── Restricted / sanctioned jurisdictions (admin-only; customer flows must filter `restricted`) ──
  { code: 'RU', name: 'Russia', flag: '🇷🇺', continent: 'Europe', restricted: true },
  { code: 'BY', name: 'Belarus', flag: '🇧🇾', continent: 'Europe', restricted: true },
  { code: 'XK', name: 'Kosovo', flag: '🇽🇰', continent: 'Europe' },
  { code: 'VA', name: 'Vatican City', flag: '🇻🇦', continent: 'Europe' },
  { code: 'IR', name: 'Iran', flag: '🇮🇷', continent: 'Middle East', restricted: true },
  { code: 'SY', name: 'Syria', flag: '🇸🇾', continent: 'Middle East', restricted: true },
  { code: 'YE', name: 'Yemen', flag: '🇾🇪', continent: 'Middle East', restricted: true },
  { code: 'PS', name: 'Palestine', flag: '🇵🇸', continent: 'Middle East' },
  { code: 'KP', name: 'North Korea', flag: '🇰🇵', continent: 'Asia', restricted: true },
  { code: 'MM', name: 'Myanmar (Burma)', flag: '🇲🇲', continent: 'Asia', restricted: true },
  { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', continent: 'Asia', restricted: true },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺', continent: 'Central America & Caribbean', restricted: true },
  { code: 'HT', name: 'Haiti', flag: '🇭🇹', continent: 'Central America & Caribbean', restricted: true },
  { code: 'BS', name: 'Bahamas', flag: '🇧🇸', continent: 'Central America & Caribbean', restricted: true },
  { code: 'BZ', name: 'Belize', flag: '🇧🇿', continent: 'Central America & Caribbean', restricted: true },
  { code: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬', continent: 'Central America & Caribbean', restricted: true },
  { code: 'AI', name: 'Anguilla', flag: '🇦🇮', continent: 'Central America & Caribbean', restricted: true },
  { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', continent: 'Central America & Caribbean', restricted: true },
  { code: 'TC', name: 'Turks and Caicos Islands', flag: '🇹🇨', continent: 'Central America & Caribbean', restricted: true },
  { code: 'VI', name: 'US Virgin Islands', flag: '🇻🇮', continent: 'Central America & Caribbean', restricted: true },
  { code: 'PA', name: 'Panama', flag: '🇵🇦', continent: 'Central America & Caribbean', restricted: true },
  { code: 'KY', name: 'Cayman Islands', flag: '🇰🇾', continent: 'Central America & Caribbean' },
  { code: 'BM', name: 'Bermuda', flag: '🇧🇲', continent: 'North America' },
  { code: 'VG', name: 'British Virgin Islands', flag: '🇻🇬', continent: 'Central America & Caribbean' },
  { code: 'AW', name: 'Aruba', flag: '🇦🇼', continent: 'Central America & Caribbean' },
  { code: 'CW', name: 'Curaçao', flag: '🇨🇼', continent: 'Central America & Caribbean' },
  { code: 'SX', name: 'Sint Maarten', flag: '🇸🇽', continent: 'Central America & Caribbean' },
  { code: 'LY', name: 'Libya', flag: '🇱🇾', continent: 'Africa', restricted: true },
  { code: 'SO', name: 'Somalia', flag: '🇸🇴', continent: 'Africa', restricted: true },
  { code: 'SD', name: 'Sudan', flag: '🇸🇩', continent: 'Africa', restricted: true },
  { code: 'ER', name: 'Eritrea', flag: '🇪🇷', continent: 'Africa', restricted: true },
  { code: 'TD', name: 'Chad', flag: '🇹🇩', continent: 'Africa', restricted: true },
  { code: 'CG', name: 'Republic of the Congo', flag: '🇨🇬', continent: 'Africa', restricted: true },
  { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', continent: 'Africa', restricted: true },
  { code: 'BI', name: 'Burundi', flag: '🇧🇮', continent: 'Africa' },
  { code: 'SC', name: 'Seychelles', flag: '🇸🇨', continent: 'Africa', restricted: true },
  { code: 'FJ', name: 'Fiji', flag: '🇫🇯', continent: 'Oceania', restricted: true },
  { code: 'WS', name: 'Samoa', flag: '🇼🇸', continent: 'Oceania', restricted: true },
  { code: 'VU', name: 'Vanuatu', flag: '🇻🇺', continent: 'Oceania', restricted: true },
  { code: 'PW', name: 'Palau', flag: '🇵🇼', continent: 'Oceania', restricted: true },
  { code: 'GU', name: 'Guam', flag: '🇬🇺', continent: 'Oceania', restricted: true },
  { code: 'AS', name: 'American Samoa', flag: '🇦🇸', continent: 'Oceania', restricted: true },
];

// Alphabetically sorted version for search/display
export const countriesSorted = [...countries].sort((a, b) => 
  a.name.localeCompare(b.name)
);

// Get countries grouped by continent
export const countriesByContinent = CONTINENT_ORDER.reduce((acc, continent) => {
  acc[continent] = countries
    .filter(c => c.continent === continent)
    .sort((a, b) => a.name.localeCompare(b.name));
  return acc;
}, {} as Record<Continent, Country[]>);

// Helper to get country by code
export const getCountryByCode = (code: string): Country | undefined => {
  return countries.find(c => c.code === code.toUpperCase());
};

// Helper to get country by name (case-insensitive)
export const getCountryByName = (name: string): Country | undefined => {
  const lower = name.toLowerCase();
  return countries.find(c => c.name.toLowerCase() === lower);
};

// Helper to get flag by code
export const getFlagByCode = (code: string): string => {
  return getCountryByCode(code)?.flag || '🏳️';
};

// Helper to get country name by code
export const getCountryNameByCode = (code: string): string => {
  return getCountryByCode(code)?.name || code;
};
